import React, { useState } from 'react';
import {
  requestLoginCode,
  loginWithCode,
  requestPasswordReset,
  resetPassword
} from './api';
import './Login.css';
import logo from './assets/Upload.svg';

function Login({ onLogin, onCreateAccount }) {
  const [mode, setMode] = useState('login');
  const [step, setStep] = useState('email');

  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });

  const [loginCode, setLoginCode] = useState('');
  const [resetCode, setResetCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  const [showPassword, setShowPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [termsRequired, setTermsRequired] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);

  const handleChange = (event) => {
    const { name, value } = event.target;

    setFormData((current) => ({
      ...current,
      [name]: value
    }));
  };

  const resetFeedback = () => {
    setError('');
    setMessage('');
  };

  const goToLogin = () => {
    setMode('login');
    setStep('email');
    setLoginCode('');
    setResetCode('');
    setNewPassword('');
    setConfirmPassword('');
    resetFeedback();
  };

  const goToCodeLogin = () => {
    setMode('code-login');
    setStep('email');
    setLoginCode('');
    resetFeedback();
  };

  const goToPasswordReset = () => {
    setMode('password-reset');
    setStep('email');
    setResetCode('');
    setNewPassword('');
    setConfirmPassword('');
    resetFeedback();
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    resetFeedback();
    setLoading(true);

    try {
      await onLogin({
        ...formData,
        acceptTerms: termsRequired && termsAccepted
      });
    } catch (loginError) {
      if (loginError.requiresTermsAcceptance) {
        setTermsRequired(true);
        setError('');
      } else {
        setError(loginError.message || 'No fue posible iniciar sesión');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleRequestLoginCode = async () => {
    resetFeedback();

    if (!formData.email) {
      setError('Escribe tu correo');
      return;
    }

    setLoading(true);

    try {
      await requestLoginCode(formData.email);
      setStep('code');
    } catch (requestError) {
      setError(
        requestError.response?.data?.error ||
        'No fue posible enviar el código'
      );
    } finally {
      setLoading(false);
    }
  };

  const handleLoginWithCode = async () => {
    resetFeedback();
    setLoading(true);

    try {
      const data = await loginWithCode({
        email: formData.email,
        code: loginCode,
        acceptTerms: termsRequired && termsAccepted
      });

      localStorage.setItem(
        'pileSession',
        JSON.stringify({
          token: data.token,
          user: data.user,
          workspace: data.workspace
        })
      );

      window.location.reload();
    } catch (codeError) {
      if (codeError.response?.data?.requiresTermsAcceptance) {
        setTermsRequired(true);
        setError('');
      } else {
        setError(
          codeError.response?.data?.error ||
          'No fue posible iniciar sesión con código'
        );
      }
    } finally {
      setLoading(false);
    }
  };

  const handleRequestPasswordReset = async () => {
    resetFeedback();

    if (!formData.email) {
      setError('Escribe tu correo');
      return;
    }

    setLoading(true);

    try {
      await requestPasswordReset(formData.email);
      setStep('reset');
    } catch (requestError) {
      setError(
        requestError.response?.data?.error ||
        'No fue posible enviar el código de recuperación'
      );
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async () => {
    resetFeedback();

    if (newPassword.length < 8) {
      setError('La contraseña debe tener al menos 8 caracteres');
      return;
    }

    if (newPassword !== confirmPassword) {
      setError('Las contraseñas no coinciden');
      return;
    }

    setLoading(true);

    try {
      await resetPassword({
        email: formData.email,
        code: resetCode,
        password: newPassword
      });

      setMode('login');
      setStep('email');
      setResetCode('');
      setNewPassword('');
      setConfirmPassword('');

      setFormData((current) => ({
        ...current,
        password: ''
      }));

      setMessage('Contraseña actualizada correctamente. Ya puedes iniciar sesión.');
    } catch (resetError) {
      setError(
        resetError.response?.data?.error ||
        'No fue posible actualizar la contraseña'
      );
    } finally {
      setLoading(false);
    }
  };

  const renderFeedback = () => (
    <>
      {error && (
        <div className="login-error">
          {error}
        </div>
      )}

      {message && (
        <div className="login-success">
          {message}
        </div>
      )}
    </>
  );

  const renderTermsAcceptance = () => {
    if (!termsRequired) return null;

    return (
      <label className="terms-acceptance">
        <input
          type="checkbox"
          checked={termsAccepted}
          onChange={(event) => setTermsAccepted(event.target.checked)}
        />

        <span>
          He leído y acepto los{' '}
          <a
            href="/terminos/"
            target="_blank"
            rel="noopener noreferrer"
          >
            Términos y Condiciones
          </a>.
        </span>
      </label>
    );
  };

  const renderLegal = () => (
    <div className="login-legal">
      <strong>© 2026 aPile. Todos los derechos reservados.</strong>
      <span>
        aPile, su marca, identidad visual, software, interfaz,
        documentación y contenidos asociados se encuentran protegidos.
      </span>
    </div>
  );

  const renderLogin = () => (
    <>
      <p>Inicia sesión para continuar</p>

      <form onSubmit={handleSubmit}>
        <input
          type="email"
          name="email"
          placeholder="Correo"
          value={formData.email}
          onChange={handleChange}
          required
        />

        <div className="password-field">
          <input
            type={showPassword ? 'text' : 'password'}
            name="password"
            placeholder="Contraseña"
            value={formData.password}
            onChange={handleChange}
            required
          />

          <button
            type="button"
            className={`password-toggle ${showPassword ? 'is-visible' : ''}`}
            onClick={() => setShowPassword((current) => !current)}
            aria-label={showPassword ? 'Ocultar contraseña' : 'Mostrar contraseña'}
            title={showPassword ? 'Ocultar contraseña' : 'Mostrar contraseña'}
          >
            <span className="password-eye-icon" aria-hidden="true"></span>
          </button>
        </div>

        {renderFeedback()}

        {renderTermsAcceptance()}

        <button
          type="submit"
          className="login-primary-button"
          disabled={loading || (termsRequired && !termsAccepted)}
        >
          {loading ? 'Entrando...' : 'Iniciar sesión'}
        </button>

        <button
          type="button"
          className="login-secondary-button"
          onClick={goToCodeLogin}
        >
          Entrar con código
        </button>

        <button
          type="button"
          className="login-text-action"
          onClick={goToPasswordReset}
        >
          Olvidé mi contraseña
        </button>

        <div className="login-register">
          <span>¿No tienes cuenta?</span>

          <button
            type="button"
            className="login-text-action"
            onClick={onCreateAccount}
          >
            Crear cuenta
          </button>
        </div>
      </form>
    </>
  );

  const renderCodeLogin = () => (
    <>
      {step === 'email' ? (
        <>
          <p>Ingresa el correo asociado a tu cuenta</p>

          <input
            type="email"
            name="email"
            placeholder="Correo"
            value={formData.email}
            onChange={handleChange}
            required
          />

          {renderFeedback()}

          <button
            type="button"
            className="login-primary-button"
            disabled={loading}
            onClick={handleRequestLoginCode}
          >
            {loading ? 'Enviando...' : 'Enviar código'}
          </button>
        </>
      ) : (
        <>
          <p>Revisa tu correo</p>

          <div className="login-email-confirmation">
            Enviamos un código a<br />
            <strong>{formData.email}</strong>
          </div>

          <input
            type="text"
            inputMode="numeric"
            autoComplete="one-time-code"
            placeholder="Código de 6 dígitos"
            value={loginCode}
            onChange={(event) =>
              setLoginCode(
                event.target.value.replace(/\D/g, '').slice(0, 6)
              )
            }
          />

          {renderFeedback()}

          {renderTermsAcceptance()}

          <button
            type="button"
            className="login-primary-button"
            disabled={
              loading ||
              loginCode.length !== 6 ||
              (termsRequired && !termsAccepted)
            }
            onClick={handleLoginWithCode}
          >
            {loading ? 'Entrando...' : 'Entrar'}
          </button>

          <button
            type="button"
            className="login-text-action code-resend-button"
            disabled={loading}
            onClick={handleRequestLoginCode}
          >
            Reenviar código
          </button>

          <button
            type="button"
            className="login-text-action code-change-email-button"
            onClick={() => {
              setStep('email');
              setLoginCode('');
              resetFeedback();
            }}
          >
            Cambiar correo
          </button>
        </>
      )}

      <button
        type="button"
        className="login-text-action back-to-login-button"
        onClick={goToLogin}
      >
        Volver al inicio de sesión
      </button>
    </>
  );

  const renderPasswordReset = () => (
    <>
      {step === 'email' ? (
        <>
          <p>Recupera tu contraseña</p>

          <div className="login-helper-text">
            Ingresa el correo asociado a tu cuenta.
          </div>

          <input
            type="email"
            name="email"
            placeholder="Correo"
            value={formData.email}
            onChange={handleChange}
            required
          />

          {renderFeedback()}

          <button
            type="button"
            className="login-primary-button"
            disabled={loading}
            onClick={handleRequestPasswordReset}
          >
            {loading ? 'Enviando...' : 'Enviar código'}
          </button>
        </>
      ) : (
        <>
          <p>Crea una nueva contraseña</p>

          <div className="login-email-confirmation">
            Enviamos un código a<br />
            <strong>{formData.email}</strong>
          </div>

          <input
            type="text"
            inputMode="numeric"
            autoComplete="one-time-code"
            placeholder="Código de 6 dígitos"
            value={resetCode}
            onChange={(event) =>
              setResetCode(
                event.target.value.replace(/\D/g, '').slice(0, 6)
              )
            }
          />

          <div className="password-field">
            <input
              type={showNewPassword ? 'text' : 'password'}
              placeholder="Nueva contraseña"
              value={newPassword}
              onChange={(event) => setNewPassword(event.target.value)}
            />

            <button
              type="button"
              className={`password-toggle ${showNewPassword ? 'is-visible' : ''}`}
              onClick={() =>
                setShowNewPassword((current) => !current)
              }
              aria-label={
                showNewPassword
                  ? 'Ocultar nueva contraseña'
                  : 'Mostrar nueva contraseña'
              }
            >
              <span className="password-eye-icon" aria-hidden="true"></span>
            </button>
          </div>

          <div className="password-field">
            <input
              type={showConfirmPassword ? 'text' : 'password'}
              placeholder="Confirmar contraseña"
              value={confirmPassword}
              onChange={(event) => setConfirmPassword(event.target.value)}
            />

            <button
              type="button"
              className={`password-toggle ${showConfirmPassword ? 'is-visible' : ''}`}
              onClick={() =>
                setShowConfirmPassword((current) => !current)
              }
              aria-label={
                showConfirmPassword
                  ? 'Ocultar confirmación'
                  : 'Mostrar confirmación'
              }
            >
              <span className="password-eye-icon" aria-hidden="true"></span>
            </button>
          </div>

          <div className="login-helper-text">
            La contraseña debe tener al menos 8 caracteres.
          </div>

          {renderFeedback()}

          <button
            type="button"
            className="login-primary-button"
            disabled={
              loading ||
              resetCode.length !== 6 ||
              newPassword.length < 8 ||
              confirmPassword.length < 8
            }
            onClick={handleResetPassword}
          >
            {loading ? 'Actualizando...' : 'Cambiar contraseña'}
          </button>

          <button
            type="button"
            className="login-text-action reset-resend-button"
            disabled={loading}
            onClick={handleRequestPasswordReset}
          >
            Reenviar código
          </button>

          <button
            type="button"
            className="login-text-action reset-change-email-button"
            onClick={() => {
              setStep('email');
              setResetCode('');
              setNewPassword('');
              setConfirmPassword('');
              resetFeedback();
            }}
          >
            Cambiar correo
          </button>
        </>
      )}

      <button
        type="button"
        className="login-text-action back-to-login-button"
        onClick={goToLogin}
      >
        Volver al inicio de sesión
      </button>
    </>
  );

  return (
    <div className="login-page">
      <div className="login-card">
        <img src={logo} alt="Pile" className="login-logo" />

        {mode === 'login' && renderLogin()}
        {mode === 'code-login' && renderCodeLogin()}
        {mode === 'password-reset' && renderPasswordReset()}

        {renderLegal()}
      </div>
    </div>
  );
}

export default Login;
